import type { FC, ReactElement } from "react"

import { QueryClient, useQueryClient } from "@tanstack/react-query"
import { type Params, useParams, useSearchParams } from "react-router-dom"

import {
    CoachNavigationStepper,
    ECoachNavigationStepperMode,
    ETypographyAlignment,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    type TCoachNavigationStepperBullet,
    Typography
} from "@/3514/components"
import { type IUseTranslation, useTranslation } from "@/hooks"

import { coachGroupSessionSummaryConfigPrefetcher } from "./coach.group.api.queries.hooks"
import styles from "./coach.group.styles"
import type { TCoachGroupSessionNavigationConfig as TNavigationConfig } from "./coach.group.ui.types"
import uiUtils from "./coach.group.ui.utils"

const { mapSessionNavigationListToStepperBulletList: getStepperBullets } = uiUtils
const { heading: classes } = styles

type TProps = {
    navConfig: TNavigationConfig[]
}
const CoachGroupHeading: FC<TProps> = ({ navConfig }: TProps): ReactElement => {
    const [searchParams]: ReturnType<typeof useSearchParams> = useSearchParams()

    const qc: QueryClient = useQueryClient()

    const { t }: IUseTranslation = useTranslation()

    const { groupId: groupIdParam }: Readonly<Params> = useParams()

    const groupId: number = Number(groupIdParam)
    const sessionId: number = Number(searchParams.get("sessionId"))

    const bullets: TCoachNavigationStepperBullet[] = getStepperBullets({
        navs: navConfig,
        groupId,
        sessionId
    })

    async function handleBulletHover(index: number): Promise<void> {
        await coachGroupSessionSummaryConfigPrefetcher({ qc }, { sessionId: index })
    }

    return (
        <section className={classes.container()}>
            <Typography
                text={t("coachSide.groupPage.heading.summary")}
                tag={ETypographyTag.Heading2}
                size={ETypographySize.Heading2}
                weight={ETypographyWeight.Medium}
                family={ETypographyFamily.DmSerif}
                alignment={ETypographyAlignment.Left}
            />

            <CoachNavigationStepper
                mode={ECoachNavigationStepperMode.Sessions}
                onListHover={handleBulletHover}
                bullets={bullets}
            />
        </section>
    )
}

CoachGroupHeading.displayName = "CoachGroupHeading"

export default CoachGroupHeading
